import { useClerk } from "@clerk/react";
import { useEffect, useRef } from "react";
import { LOGIN } from "settings/constants";

const SESSION_LIMIT_MS = 30 * 60 * 1000;
const POST_LIMIT_IDLE_MS = 30 * 1000;
const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_THROTTLE_MS = 500;
const SESSION_START_KEY = "sessionStartedAt";
const LAST_ACTIVITY_KEY = "lastActivityAt";

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

export const useIdleLogout = (enabled: boolean) => {
  const { signOut } = useClerk();
  const lastWriteRef = useRef(0);
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      localStorage.removeItem(SESSION_START_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return;
    }

    signingOutRef.current = false;

    if (!localStorage.getItem(SESSION_START_KEY)) {
      localStorage.setItem(SESSION_START_KEY, String(Date.now()));
    }
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));

    const markActive = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };

    const logoutIfExpiredAndIdle = async () => {
      if (signingOutRef.current) return;

      const sessionStartedAt = Number(
        localStorage.getItem(SESSION_START_KEY) || Date.now(),
      );
      if (Date.now() - sessionStartedAt < SESSION_LIMIT_MS) return;

      const tabIsHidden = document.visibilityState !== "visible";
      if (!tabIsHidden) {
        const lastActivityAt = Number(
          localStorage.getItem(LAST_ACTIVITY_KEY) || 0,
        );
        if (Date.now() - lastActivityAt < POST_LIMIT_IDLE_MS) return;
      }

      signingOutRef.current = true;
      localStorage.removeItem("ldapToken");
      localStorage.removeItem(SESSION_START_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      try {
        await signOut({ redirectUrl: LOGIN });
      } catch (e) {
        console.error("Session timeout logout failed", e);
        signingOutRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      logoutIfExpiredAndIdle();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, markActive, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(
      logoutIfExpiredAndIdle,
      CHECK_INTERVAL_MS,
    );
    logoutIfExpiredAndIdle();

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, markActive);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, signOut]);
};
